import React, { createContext, useContext, useState, useEffect } from "react";
import { enTranslations } from "@/translations/en";
import { arTranslations } from "@/translations/ar";

type Language = "en" | "ar";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any; // updated from string to any
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "en";
  });

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    localStorage.setItem("language", language);
  }, [language]);

  // Updated t() function to return any (supports string | string[] | object)
  const t = (key: string): any => {
    const translations = language === "ar" ? arTranslations : enTranslations;
    return key.split(".").reduce((obj: any, part: string) => obj?.[part], translations) ?? key;
  };

  const changeLanguage = (lang: Language) => {
    console.log("Language changed to:", lang);
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
}
